import { Injectable } from '@angular/core';

import { lastValueFrom, map } from 'rxjs';

import { Category, ObjectBrowserFilter, ObjectBrowserFilterType, ResultPage } from '@mirage-projects/cw-models';

import { CategoryService } from '../../abstract-classes/category.service';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { UiConfigurationLayers } from '../ui-configuration/ui-configuration';
import { UiConfigurationService } from '../ui-configuration/ui-configuration.service';

/**
 * Business logic for get the layers filters for Layer Browser
 */
@Injectable({ providedIn: 'root' })
export class LayerListFilterService {
    private layersConfig: UiConfigurationLayers | undefined = this.uiConfiguration.uiConfiguration.layers;
    constructor(
        private uiConfiguration: UiConfigurationService,
        private categoryService: CategoryService,
        private localStorageService: LocalStorageService
    ) {}

    /** Initalize object browser filter data */
    initFilters(): ObjectBrowserFilter[] {
        const filters: ObjectBrowserFilter[] = [];

        if (!this.layersConfig?.filters || this.layersConfig.filters.includes('name')) {
            filters.push({
                type: ObjectBrowserFilterType.SEARCH,
                fieldName: 'name',
                queryField: 'search',
                placeholder: 'LAYER_BROWSER.TYPE_LAYER_NAME',
            });
        }

        if (!this.layersConfig?.filters || this.layersConfig.filters.includes('layerType')) {
            const selected = '';
            filters.push({
                type: ObjectBrowserFilterType.SELECT,
                fieldName: 'layerType',
                queryField: 'layer_type',
                text: 'LAYERS.SORT_BY',
                selected,
                value: selected,
                range: [
                    {
                        value: '',
                        text: 'LAYERS.LAYER_TYPE_OPC0',
                    },
                    {
                        value: 'PointCloud',
                        text: 'LAYERS.LAYER_TYPE_OPC1',
                    },
                    {
                        value: 'VectorLayer',
                        text: 'LAYERS.LAYER_TYPE_OPC2',
                    },
                    {
                        value: 'RasterLayer',
                        text: 'LAYERS.LAYER_TYPE_OPC3',
                    },
                ],
            });
        }

        if (!this.layersConfig?.filters || this.layersConfig.filters.includes('category')) {
            filters.push({
                type: ObjectBrowserFilterType.SELECT,
                fieldName: 'category',
                queryField: 'category',
                text: 'LAYERS.CATEGORY_BY',
                range: [
                    {
                        value: undefined as any,
                        text: 'LAYERS.LAYER_TYPE_OPC0',
                    },
                ],
                hide: () => {
                    let hide = true;
                    if (filters[1].value === 'VectorLayer') {
                        hide = false;
                    } else {
                        (this as any).value = '';
                    }
                    return hide;
                },
                width: '12rem',
            });
        }

        if (!this.layersConfig?.filters || this.layersConfig.filters.includes('ordering')) {
            const value = this.localStorageService.getItem('layersOrder') ?? '-created_at';

            filters.push({
                type: ObjectBrowserFilterType.SELECT,
                fieldName: 'ordering',
                queryField: 'ordering',
                text: 'OBJECT_BROWSER.ORDER_BY',
                selected: value,
                value,
                range: [
                    {
                        text: 'OBJECT_BROWSER.ORDER_BY_NAME_ASC',
                        value: 'name',
                    },
                    {
                        text: 'OBJECT_BROWSER.ORDER_BY_NAME_DESC',
                        value: '-name',
                    },
                    {
                        text: 'OBJECT_BROWSER.ORDER_BY_CREATED_AT_ASC',
                        value: 'created_at',
                    },
                    {
                        text: 'OBJECT_BROWSER.ORDER_BY_CREATED_AT_DESC',
                        value: '-created_at',
                    },
                    {
                        text: 'OBJECT_BROWSER.ORDER_BY_UPDATED_AT_ASC',
                        value: 'updated_at',
                    },
                    {
                        text: 'OBJECT_BROWSER.ORDER_BY_UPDATED_AT_DESC',
                        value: '-updated_at',
                    },
                ],
                onChange: (data?: ObjectBrowserFilter): void => {
                    setTimeout(() => {
                        this.localStorageService.setItem('layersOrder', data?.value, true);
                    });
                },
                width: '15rem',
            });
        }

        if (!this.layersConfig?.filters || this.layersConfig.filters.includes('onlyUser')) {
            filters.push({
                type: ObjectBrowserFilterType.TOGGLE,
                fieldName: 'onlyUser',
                queryField: 'onlyMyLayers',
                text: 'LAYERS.SHOW_MY_LAYERS',
                tooltip: 'LAYERS.SHOW_MY_LAYERS_TOOLTIP',
                trueValue: true,
                defaultValue: false,
                value: false,
            });
        }

        if (!this.layersConfig?.filters || this.layersConfig.filters.includes('onlyOrphan')) {
            filters.push({
                type: ObjectBrowserFilterType.TOGGLE,
                fieldName: 'onlyOrphan',
                queryField: 'projectsCount',
                text: 'LAYERS.SHOW_ORPHAN_LAYERS',
                tooltip: 'LAYERS.SHOW_ORPHAN_LAYERS_TOOLTIP',
                trueValue: 0,
                defaultValue: false,
                value: false,
            });
        }

        this.updateCategories(filters);

        return filters;
    }

    /* update categories with the stored in the server */
    async updateCategories(filters: ObjectBrowserFilter[]): Promise<void> {
        const list = await lastValueFrom(
            this.categoryService.getCategoryList().pipe(map((response: ResultPage<Category>) => response.results))
        );

        filters.forEach((filter: ObjectBrowserFilter) => {
            if (filter.fieldName === 'category') {
                list.forEach((element: Category) => {
                    if (!this.isLayerCategoryHidden(element.name)) {
                        const range = {
                            value: element.name,
                            text: `LAYERS.CATEGORY_FILTER_${element.name.replace(' ', '_').toUpperCase()}`,
                        };

                        filter.range?.push(range);
                    }
                });
            }
        });
    }

    private isLayerCategoryHidden(category: any): boolean {
        return this.layersConfig?.hiddenCategories?.includes(category) ?? false;
    }
}
